/**
 * SaaPage — the all-roles SAA analysis surface (#4), reached from the "SAA" tile on the
 * Welcome dashboard. Distinct from Admin → SAA, which remains the Admin-only INPUT surface
 * (allocation editing, daily-returns import, profile management).
 *
 * Reads the on-record SAA daily-return series (`saa_daily_return`, mig 0084) for analysis —
 * reads are open to any authenticated user (SAA is firm-owned reference data, no client PII).
 * Tick one or more profiles (auto-refreshes on every change) + an optional period; the
 * analytics view (lazy — keeps Vega off the initial bundle, C-260) renders the stats,
 * cumulative + drawdown graphs, the two return-vs-risk scatters, and a combined year×month
 * returns table. Statistics are on a DAILY basis (Pedro 2026-09-13): annualised vol =
 * stdev(daily)×√252, drawdown on the daily series.
 */
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import {
  Alert, Card, Checkbox, Container, Group, Loader, SimpleGrid, Stack, Text, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';

import { saaApi, type SaaAnalytics } from '../api/saa';
import { BackToCompliance } from '../components/BackToCompliance';
import {
  SAA_DEFAULT_PRESET, SaaPeriodSelect, dayOf, saaPeriodQuery, type SaaPeriodPreset,
} from '../components/SaaPeriodSelect';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const SaaAnalyticsView = lazy(() => import('../components/SaaAnalyticsView'));
const PROFILES_KEY = ['saa', 'profiles'] as const;
const errMsg = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong.');

export function SaaPage() {
  useDocumentTitle('SAA');
  const { data: profiles } = useQuery({ queryKey: PROFILES_KEY, queryFn: () => saaApi.profiles() });

  const [codes, setCodes] = useState<string[]>([]);
  // P32: the period is a preset (default Last 20yr), Max, or a From/To date range.
  const [preset, setPreset] = useState<SaaPeriodPreset>(SAA_DEFAULT_PRESET);
  const [rangeFrom, setRangeFrom] = useState<Date | null>(null);
  const [rangeTo, setRangeTo] = useState<Date | null>(null);
  const period = saaPeriodQuery(preset, rangeFrom, rangeTo);
  const periodKey = JSON.stringify(period);
  const [result, setResult] = useState<SaaAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  // The Range pickers stay inside the stored data of the ticked profiles (Pedro 2026-10-13):
  // earliest first return → latest last return, from the last analysis loaded.
  const firsts = (result?.profiles ?? []).map((p) => p.first).filter((d): d is string => !!d).sort();
  const lasts = (result?.profiles ?? []).map((p) => p.last).filter((d): d is string => !!d).sort();
  const dataFirst = dayOf(firsts[0]);
  const dataLast = dayOf(lasts[lasts.length - 1]);
  const seqRef = useRef(0);

  // The canonical order — the profile list's own sort order (top → bottom).
  const orderedCodes = (profiles ?? []).map((p) => p.code);

  // Auto-refresh whenever the selection or the period changes. Latest-wins guard so a
  // fast series of toggles can't land an out-of-order (stale) response.
  useEffect(() => {
    if (codes.length === 0) { setResult(null); return; }
    if (period === null) return; // Range with a month still missing: keep the last result
    const seq = ++seqRef.current;
    setLoading(true);
    saaApi.returnsAnalytics(codes, period.from, period.to, period.years)
      .then((data) => {
        if (seq !== seqRef.current) return;
        if (!data || !Array.isArray(data.profiles)) {
          throw new Error('Unexpected response — is the server up to date? Try reloading.');
        }
        setResult(data);
      })
      .catch((err) => {
        if (seq !== seqRef.current) return;
        setResult(null);
        notifications.show({ color: 'red', title: 'Could not load analytics', message: errMsg(err) });
      })
      .finally(() => { if (seq === seqRef.current) setLoading(false); });
    // `period` is a fresh object every render; periodKey (its JSON) is the stable dependency.
  }, [codes, periodKey]);

  return (
    <Container size="xl" pt="md">
      <Stack>
        <BackToCompliance to="/" label="Back to Welcome" />
        <div>
          <Title order={3}>SAA</Title>
          <Text size="sm" c="dimmed">
            Analyse the firm's Strategic Asset Allocation return series — compare profiles,
            statistics, drawdowns and return-vs-risk. Reference data, available to everyone;
            the models are edited by Admin under Admin → SAA.
          </Text>
        </div>

        <Card withBorder padding="lg">
          <Stack>
            <Group justify="space-between" align="center">
              <Text size="sm" fw={500}>Profiles</Text>
              {loading && <Loader size="xs" />}
            </Group>
            <Checkbox.Group value={codes} onChange={setCodes}>
              <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="xs" verticalSpacing="xs">
                {(profiles ?? []).map((p) => (
                  <Checkbox key={p.code} value={p.code} label={p.label} />
                ))}
              </SimpleGrid>
            </Checkbox.Group>
            <Stack gap={4}>
              <Text size="sm" fw={500}>Period</Text>
              <SaaPeriodSelect
                preset={preset} onPreset={setPreset}
                rangeFrom={rangeFrom} onRangeFrom={setRangeFrom}
                rangeTo={rangeTo} onRangeTo={setRangeTo}
                dataFirst={dataFirst} dataLast={dataLast}
              />
              <Text size="xs" c="dimmed">
                "Last N yr" ends at each profile's latest stored day; Max is each profile's full history.
              </Text>
            </Stack>
          </Stack>
        </Card>

        {codes.length === 0 && (
          <Text size="sm" c="dimmed">Tick one or more profiles above to see the analysis.</Text>
        )}
        {result && (
          <Suspense fallback={<Group justify="center" p="xl"><Loader /></Group>}>
            {result.profiles.length === 0
              ? <Alert color="gray">No data for the selected profile(s) in this period.</Alert>
              : <SaaAnalyticsView profiles={result.profiles} orderedCodes={orderedCodes} />}
          </Suspense>
        )}
      </Stack>
    </Container>
  );
}
